//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import SpeciesTypeModel from './speciesType.model';

/**
 * @service SpeciesTypeService
 * @description Service for managing Species Type definitions within the aquaculture system.
 * Species Types define the specific biological organisms being cultured (e.g., *Litopenaeus vannamei*, *Penaeus monodon*, *Tilapia*).
 *
 * This service provides the interface to retrieve these definitions from the backend API.
 * It is essential for:
 * 1. **Culture Planning**: Selecting the correct species when starting a new culture cycle.
 * 2. **Parameter Management**: Different species have different optimal water quality ranges (QP Ranges).
 * 3. **Reporting**: Aggregating production data by species.
 *
 * The service interacts with the `/aqu/speciestype/` backend endpoint.
 *
 * @feature 1. Retrieve all defined Species Types.
 * @feature 2. Filter species by Culture Category (e.g., Shrimp vs. Fish).
 * @feature 3. Get species usage counts and details for specific farm sites.
 * @feature 4. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<SpeciesTypeModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/speciestype/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class SpeciesTypeService extends Service<SpeciesTypeModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/speciestype/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/speciestype/');
  }

  /**
   * @method getAllSpeciesType
   * @async
   * @description Fetch all Species Type records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all species definitions are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<SpeciesTypeModel>>} Promise resolving to an array of SpeciesTypeModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * speciesTypeService.getAllSpeciesType().then(species => {
   *   console.log('Available Species:', species);
   * });
   */
  public getAllSpeciesType() {
    return new Promise<Array<SpeciesTypeModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<SpeciesTypeModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSpeciesTypesWithCC
   * @async
   * @description Fetch Species Types including their associated Culture Category details.
   *
   * This endpoint likely returns a joined view, useful for displaying species grouped by category (e.g., Shrimp, Fish).
   *
   * @returns {Promise<Array<SpeciesTypeModel>>} Promise resolving to the list of species with category info.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request to `cultureCategory`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getSpeciesTypesWithCC() {
    return new Promise<Array<SpeciesTypeModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<SpeciesTypeModel>>(`${this.baseUrl}${this.serviceUrl}cultureCategory`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSpeciesCount
   * @async
   * @description Fetch the count of species types currently in use at specific farm sites.
   *
   * @param {SpeciesTypeModel[]} farmSiteIds - Array of models containing farm site IDs to query.
   * @returns {Promise<SpeciesTypeModel>} Promise resolving to the count data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `count/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getSpeciesCount(farmSiteIds: SpeciesTypeModel[]) {
    return new Promise<SpeciesTypeModel>((resolve, reject) => {
      const request = this.httpClient.post<SpeciesTypeModel>(`${this.baseUrl}${this.serviceUrl}count/farmSite`, {
        farmSite: farmSiteIds
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSpeciesTypeByFarmSites
   * @async
   * @description Fetch detailed species information for specific farm sites.
   *
   * @param {SpeciesTypeModel[]} farmSiteIds - Array of models containing farm site IDs to query.
   * @returns {Promise<SpeciesTypeModel>} Promise resolving to the species details.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `speciesDetails/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getSpeciesTypeByFarmSites(farmSiteIds: SpeciesTypeModel[]) {
    return new Promise<SpeciesTypeModel>((resolve, reject) => {
      const request = this.httpClient.post<SpeciesTypeModel>(`${this.baseUrl}${this.serviceUrl}speciesDetails/farmSite`, {
        farmSite: farmSiteIds
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSpeciesTypeByCultureCategoryId
   * @async
   * @description Fetch all species types belonging to a specific Culture Category ID.
   *
   * @param {number} cultureCategoryId - The unique identifier of the culture category.
   * @returns {Promise<Array<SpeciesTypeModel>>} Promise resolving to the filtered list of species.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getSpeciesTypeByCultureCategoryId(cultureCategoryId: number) {
    return new Promise<Array<SpeciesTypeModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<SpeciesTypeModel>>(`${this.baseUrl}${this.serviceUrl}cultureCategoryId/${cultureCategoryId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
